// URL/slug helpers for park detail pages. PURE functions (no data access) so
// they run on both the server and the client.
//
// Park URLs are `/parks/{name-slug}-{id}` — e.g. `/parks/high-park-toronto-1010`.
// The id (always `toronto-<n>` in cache mode) is the canonical, unique, stable
// key; the name-slug is a human/SEO-friendly prefix. Name-only URLs are not an
// option: 44% of parks share a name (e.g. "City Wide Open Space" ×471).
//
// The data-aware resolver that turns a URL slug back into an id — including
// old id-only links and stale name prefixes — lives in src/lib/parks.ts
// (resolveParkId), because it needs the loaded park list.

export function slugifyName(name: string): string {
  return String(name || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // strip combining accents
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Canonical slug for a park: `{name-slug}-{id}`. If the name slugifies to empty
// (blank or non-latin), fall back to the bare id so the URL is still valid.
export function parkSlug(name: string, id: string): string {
  const s = slugifyName(name);
  return s ? `${s}-${id}` : id;
}

// Canonical `/parks/...` path for a park. Use this at link sites that have the
// park name; sites that only have an id use parkHref(id) from lib/parks.ts.
export function parkPath(name: string, id: string): string {
  return `/parks/${parkSlug(name, id)}`;
}

// Resolve a URL slug back to a park id against the published park list. Handles
// the canonical `{name}-{id}` slug, a legacy id-only URL (`/parks/toronto-1010`),
// a wrong or stale name prefix (right id, wrong name), and a retired id with a
// successor in `redirects`. Returns null if no such park.
//
// PURE so the page (via resolveParkId in lib/parks.ts) and the proxy's early
// redirect (lib/park-canonical.ts) share one rule. Two copies would drift, and
// the symptom of drift is a redirect loop between them.
export function resolveSlugToId(
  slug: string,
  bySlug: ReadonlyMap<string, string>,
  ids: ReadonlySet<string>,
  successorOf: (slug: string) => string | null,
): string | null {
  const direct = bySlug.get(slug);
  if (direct) return direct;
  // Not the canonical slug — extract the trailing `toronto-<n>` id (cache mode)
  // or accept the whole slug as an id (sample mode / bare id-only URL).
  const m = slug.match(/(toronto-\d+)$/);
  if (m && ids.has(m[1])) return m[1];
  if (ids.has(slug)) return slug;
  // Generic fallback for a non-canonical URL whose id isn't the `toronto-<n>`
  // form (OSM ids, sample mode). Rare, so an O(n) scan for the known id the
  // slug ends with is fine.
  for (const id of ids) {
    if (slug.endsWith(`-${id}`)) return id;
  }
  // A retired id with a successor. Returning the successor's id makes the
  // caller's canonical-slug check issue the 308 to it.
  const successor = successorOf(slug);
  return successor && ids.has(successor) ? successor : null;
}
